import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FlowStatus } from '../interfaces/Iip-flow-status';
import IpErrorResponse from '../interfaces/ip-error-response';
import { FlowStatusCreation } from '../interfaces/ip-flow-status-creation';
import { FlowStatusEdition } from '../interfaces/ip-flow-status-edition';
import { RoleResponse } from '../interfaces/ip-role-response';
import { DomainType } from '../types/ip-domain-type';
import { IpAuthService } from './ip-auth.service';
import { IpSnackbarService } from './ip-snackbar.service';

@Injectable({
  providedIn: 'root'
})
export class FlowStatusService {
  api = environment.api;
  private pendingChanges = false;
  private flowStatusesSubject: BehaviorSubject<FlowStatus[] | null> =
    new BehaviorSubject<FlowStatus[] | null>(null);
  public currentFlowStatuses$: Observable<FlowStatus[] | null> =
    this.flowStatusesSubject.asObservable();

  constructor(
    private http: HttpClient,
    private ipAuthService: IpAuthService,
    private ipSnackbarService: IpSnackbarService,
  ) {
    this.ipAuthService.currentSession$.subscribe(session => {
      if (session) {
        this.setFlowStatuses();
      }
    });
  }

  getFlowStatuses(
    params?: {
      typeDomain?: DomainType,
      subsidiaryId?: number
    }
  ): Observable<FlowStatus[]> {
    return this.http.get<FlowStatus[]>(
      `${this.api}/invopay/flow-statuses`,
      { params }
    );
  }

  setFlowStatuses() {
    this.getFlowStatuses().subscribe({
      next: flowsStatuses => {
        this.flowStatusesSubject.next(flowsStatuses);
      },
      error: (e: IpErrorResponse) => {
        this.ipAuthService.logOut();
        this.ipSnackbarService.showErrorMessage(e);
      },
    });
  }

  getFlowStatusesWithRoles(
    params?: {
      typeDomain?: DomainType,
      subsidiaryId?: number
    }
  ): Observable<FlowStatus[]> {
    return this.http.get<FlowStatus[]>(
      `${this.api}/invopay/flow-statuses/roles`,
      { params }
    );
  }

  createFlowStatus(flowStatus: FlowStatusCreation): Observable<FlowStatus> {
    return this.http.post<FlowStatus>(
      `${this.api}/invopay/flow-statuses`,
      flowStatus
    );
  }

  updateFlowStatus(
    id: number,
    flowStatus: FlowStatusEdition,
  ): Observable<FlowStatus> {
    return this.http.put<FlowStatus>(
      `${this.api}/invopay/flow-statuses/${id}`,
      flowStatus
    );
  }

  deleteFlowStatus(id: number): Observable<FlowStatus> {
    return this.http.delete<FlowStatus>(
      `${this.api}/invopay/flow-statuses/${id}`
    );
  }

  // FLOW STATUSES' ROLES

  getAvailableRolesById(flowStatusId: number): Observable<RoleResponse[]> {
    return this.http.get<RoleResponse[]>(
      `${this.api}/invopay/flow-statuses/${flowStatusId}/roles/available`
    );
  }

  getAssignedRolesById(flowStatusId: number): Observable<RoleResponse[]> {
    return this.http.get<RoleResponse[]>(
      `${this.api}/invopay/flow-statuses/${flowStatusId}/roles/assigned`
    );
  }

  assignRoleToFlowStatus(
    flowStatusId: number,
    roleId: number
  ): Observable<void> {
    return this.http.post<void>(
      `
        ${this.api}/invopay/flow-statuses/${flowStatusId}/roles/assign/${roleId}
      `,
      {}
    );
  }

  deleteRoleFromFlowStatus(
    flowStatusId: number,
    roleId: number
  ): Observable<void> {
    return this.http.delete<void>(
      `
        ${this.api}/invopay/flow-statuses/${flowStatusId}/roles/assign/${roleId}
      `,
      {}
    );
  }

  // These methods are made for checking changes to update (or not to update)
  // the config table's data after role assignment(s)

  getPendingChanges() {
    return this.pendingChanges;
  }

  setPendingChanges() {
    this.pendingChanges = true;
  }

  resetPendingChanges() {
    this.pendingChanges = false;
  }
}
